import React, { useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * TopMerchantsPieChart Component
 * 
 * Shows a pie chart of top merchants by total spending
 */
const TopMerchantsPieChart = ({ transactions = [], tooltip = null, limit = 5 }) => {
  // Calculate merchant totals
  const merchantData = useMemo(() => {
    const merchantMap = new Map();
    
    transactions.forEach(tx => {
      // Only count expense transactions (negative amounts)
      if (tx.type === 'transfer' || tx.amount >= 0) return;
      
      const amount = Math.abs(tx.amount || 0);
      if (amount === 0) return;
      
      // Extract merchant name from description
      let merchantName = tx.description || 'Unknown';
      
      // Clean up common patterns
      merchantName = merchantName
        .replace(/\d{2}\/\d{2}\/\d{2,4}/, '') // Remove dates
        .replace(/\*+\d+/, '') // Remove card numbers
        .replace(/^(DEBIT|CREDIT|ACH|CHK|ATM|POS)\s+/i, '') // Remove transaction type prefixes
        .trim();
      
      // Take first 2-3 words as merchant name
      const words = merchantName.split(/\s+/);
      merchantName = words.slice(0, Math.min(3, words.length)).join(' ');
      
      // Limit length
      if (merchantName.length > 25) {
        merchantName = merchantName.substring(0, 25) + '...';
      }
      
      if (!merchantName || merchantName === 'Unknown') return;
      
      const current = merchantMap.get(merchantName) || 0;
      merchantMap.set(merchantName, current + amount);
    });
    
    // Convert to array and sort by total spending
    const sortedMerchants = Array.from(merchantMap.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
    
    // Get top N merchants
    const topMerchants = sortedMerchants.slice(0, limit);
    
    // Calculate "Other" if needed
    const topTotal = topMerchants.reduce((sum, m) => sum + m.total, 0);
    const grandTotal = sortedMerchants.reduce((sum, m) => sum + m.total, 0);
    const otherTotal = grandTotal - topTotal;
    
    if (otherTotal > 0 && sortedMerchants.length > limit) {
      topMerchants.push({ name: 'Other', total: otherTotal });
    }
    
    // Calculate percentages
    const withPercentages = topMerchants.map(m => ({
      ...m,
      percentage: grandTotal > 0 ? (m.total / grandTotal) * 100 : 0
    }));
    
    return {
      merchants: withPercentages,
      total: grandTotal,
      count: transactions.filter(tx => tx.type !== 'transfer' && tx.amount < 0).length
    };
  }, [transactions, limit]);
  
  // Color palette with actual hex values for conic-gradient
  const colors = [
    { hex: '#3b82f6', bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100' },
    { hex: '#10b981', bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' },
    { hex: '#f59e0b', bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' },
    { hex: '#f97316', bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-100' },
    { hex: '#ef4444', bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' },
    { hex: '#9ca3af', bg: 'bg-gray-400', text: 'text-gray-600', light: 'bg-gray-100' }
  ];
  
  // Build pie chart using a single conic-gradient
  const renderPieChart = () => {
    // Build the conic-gradient string with all segments
    let gradientParts = [];
    let cumulativePercentage = 0;
    
    merchantData.merchants.forEach((merchant, index) => {
      const color = colors[index % colors.length];
      const startPercentage = cumulativePercentage;
      const endPercentage = cumulativePercentage + merchant.percentage;
      
      // Add color stop for this segment
      gradientParts.push(`${color.hex} ${startPercentage}% ${endPercentage}%`);
      
      cumulativePercentage = endPercentage;
    });
    
    // Build the complete gradient string
    const gradientString = `conic-gradient(from 0deg, ${gradientParts.join(', ')})`;
    
    return (
      <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full overflow-hidden flex-shrink-0 shadow-md" style={{ transform: 'rotate(-90deg)' }}>
        {/* Single div with complete conic gradient */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: gradientString
          }}
        />
      </div>
    );
  };
  
  return (
    <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_35px_-8px_rgba(0,0,0,0.4)] rounded-xl p-2 sm:p-3 lg:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] min-h-[100px] sm:min-h-[120px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 sm:mb-3 flex-shrink-0">
        <h3 className="text-xs sm:text-sm font-medium text-gray-600 truncate flex-1">
          Top Merchants
        </h3>
        {tooltip && (
          <div className="relative group flex-shrink-0">
            <InformationCircleIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg invisible group-hover:visible transition-all pointer-events-none z-10">
              <p className="font-normal leading-relaxed">
                {tooltip}
              </p>
              <div className="absolute top-full right-4 border-8 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      {merchantData.merchants.length > 0 ? (
        <div className="flex-1 flex items-center gap-3 sm:gap-4">
          {/* Pie Chart */}
          <div className="flex items-center justify-center">
            {renderPieChart()}
          </div>
          
          {/* Legend */}
          <div className="flex-1 flex flex-col justify-center space-y-1.5 sm:space-y-2 min-w-0">
            {merchantData.merchants.map((merchant, index) => {
              const color = colors[index % colors.length];
              return (
                <div key={index} className="flex items-center gap-2 min-w-0">
                  <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${color.bg} flex-shrink-0`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium text-gray-700 truncate">
                        {merchant.name}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {merchant.percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="text-xs font-semibold text-gray-800">
                      ${merchant.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No merchant data
        </div>
      )}
      
      {/* Footer */}
      <div className="mt-2 sm:mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
        {merchantData.count} expense transactions
      </div>
    </div>
  );
};

export default TopMerchantsPieChart;

